// ---------------------------------------------------------------------------
// Business Verification Page — Multi-step KYB verification flow (overhauled)
// ---------------------------------------------------------------------------

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ApiClientError } from '@noxverify/api-client';
import type { applicant } from '@noxverify/api-contracts';
import { StepIndicator, type Step } from '../components/StepIndicator';
import { FileUpload } from '../components/FileUpload';
import { Footer } from '../components/Footer';
import {
  UboDeclarationForm,
  type DeclaredUbo,
} from '../components/UboDeclarationForm';
import { getApiClient } from '../lib/api';

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

function getKybSteps(t: TFunction): Step[] {
  return [
    { key: 'business', label: t('stepIndicator.steps.business') },
    { key: 'officers', label: t('stepIndicator.steps.officers') },
    { key: 'ubos', label: t('stepIndicator.steps.ubos') },
    { key: 'documents', label: t('stepIndicator.steps.documents') },
    { key: 'representative', label: t('stepIndicator.steps.representative') },
    { key: 'review', label: t('stepIndicator.steps.review') },
  ];
}

const STEP_ICONS: Record<string, React.ReactNode> = {
  business: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"
        />
      </svg>
    ),
  officers: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
        />
      </svg>
    ),
  ubos: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z"
        />
      </svg>
    ),
  documents: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
        />
      </svg>
    ),
  representative: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
        />
      </svg>
    ),
  review: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
};

function getStepMeta(t: TFunction, key: string): { title: string; subtitle: string; icon: React.ReactNode } {
  return {
    title: t(`businessVerification.steps.${key}.title`),
    subtitle: t(`businessVerification.steps.${key}.subtitle`),
    icon: STEP_ICONS[key] ?? null,
  };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Address {
  line1: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

function emptyAddress(): Address {
  return { line1: '', city: '', state: '', postalCode: '', country: '' };
}

interface BusinessDetails {
  legalName: string;
  tradingName: string;
  registrationNumber: string;
  jurisdiction: string;
  entityType: string;
  incorporationDate: string;
  taxId: string;
  lei: string;
  websiteUrl: string;
  industryCode: string;
  businessActivity: string;
  registeredAddress: Address;
  tradingAddressSameAsRegistered: boolean;
  tradingAddress: Address;
  annualRevenueRange: string;
}

interface OfficerEntry {
  key: string;
  name: string;
  role: string;
  nationality: string;
  dateOfBirth: string;
  residentialAddress: Address;
  idDocument: File | null;
}

/** Extension data for each UBO managed alongside the UboDeclarationForm. */
interface UboExtension {
  residentialAddress: Address;
  natureOfControl: string;
  idDocument: File | null;
}

interface DocumentSlot {
  key: string;
  label: string;
  required: boolean;
  conditional: boolean;
  helperText: string;
  file: File | null;
}

interface AuthorizedRepresentative {
  fullName: string;
  role: string;
  email: string;
  phone: string;
  confirmedAuthorized: boolean;
  authorizationEvidence: File | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateKey(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function getEntityTypes(t: TFunction): { value: string; label: string }[] {
  return [
    { value: 'limited_company', label: t('businessVerification.options.entityTypes.limited_company') },
    { value: 'llp', label: t('businessVerification.options.entityTypes.llp') },
    { value: 'plc', label: t('businessVerification.options.entityTypes.plc') },
    { value: 'sole_trader', label: t('businessVerification.options.entityTypes.sole_trader') },
    { value: 'partnership', label: t('businessVerification.options.entityTypes.partnership') },
    { value: 'other', label: t('businessVerification.options.entityTypes.other') },
  ];
}

function getOfficerRoles(t: TFunction): { value: string; label: string }[] {
  return [
    { value: 'director', label: t('businessVerification.options.officerRoles.director') },
    { value: 'secretary', label: t('businessVerification.options.officerRoles.secretary') },
    { value: 'ceo', label: t('businessVerification.options.officerRoles.ceo') },
    { value: 'cfo', label: t('businessVerification.options.officerRoles.cfo') },
    { value: 'treasurer', label: t('businessVerification.options.officerRoles.treasurer') },
    { value: 'other', label: t('businessVerification.options.officerRoles.other') },
  ];
}

function getRevenueRanges(t: TFunction): { value: string; label: string }[] {
  return [
    { value: '', label: t('businessVerification.options.selectRange') },
    { value: 'lt_100k', label: t('businessVerification.options.revenueRanges.lt_100k') },
    { value: '100k_1m', label: t('businessVerification.options.revenueRanges.100k_1m') },
    { value: '1m_10m', label: t('businessVerification.options.revenueRanges.1m_10m') },
    { value: '10m_100m', label: t('businessVerification.options.revenueRanges.10m_100m') },
    { value: 'gt_100m', label: t('businessVerification.options.revenueRanges.gt_100m') },
  ];
}

function getNatureOfControlOptions(t: TFunction): { value: string; label: string }[] {
  return [
    { value: '', label: t('businessVerification.options.selectNatureOfControl') },
    { value: 'direct_shareholding', label: t('businessVerification.options.natureOfControl.direct_shareholding') },
    { value: 'indirect_shareholding', label: t('businessVerification.options.natureOfControl.indirect_shareholding') },
    { value: 'voting_rights', label: t('businessVerification.options.natureOfControl.voting_rights') },
    { value: 'appointment_of_board', label: t('businessVerification.options.natureOfControl.appointment_of_board') },
    { value: 'other', label: t('businessVerification.options.natureOfControl.other') },
  ];
}

function getRepresentativeRoles(t: TFunction): { value: string; label: string }[] {
  return [
    { value: '', label: t('businessVerification.options.selectRole') },
    { value: 'director', label: t('businessVerification.options.representativeRoles.director') },
    { value: 'officer', label: t('businessVerification.options.representativeRoles.officer') },
    { value: 'authorized_signatory', label: t('businessVerification.options.representativeRoles.authorized_signatory') },
    { value: 'legal_representative', label: t('businessVerification.options.representativeRoles.legal_representative') },
  ];
}

function getInitialDocSlots(t: TFunction): DocumentSlot[] {
  return [
    {
      key: 'cert-incorporation',
      label: t('businessVerification.documents.certIncorporation.label'),
      required: true,
      conditional: false,
      helperText: t('businessVerification.documents.certIncorporation.helper'),
      file: null,
    },
    {
      key: 'articles',
      label: t('businessVerification.documents.articles.label'),
      required: true,
      conditional: false,
      helperText: t('businessVerification.documents.articles.helper'),
      file: null,
    },
    {
      key: 'shareholder-register',
      label: t('businessVerification.documents.shareholderRegister.label'),
      required: true,
      conditional: false,
      helperText: t('businessVerification.documents.shareholderRegister.helper'),
      file: null,
    },
    {
      key: 'proof-address',
      label: t('businessVerification.documents.proofAddress.label'),
      required: true,
      conditional: false,
      helperText: t('businessVerification.documents.proofAddress.helper'),
      file: null,
    },
    {
      key: 'board-resolution',
      label: t('businessVerification.documents.boardResolution.label'),
      required: false,
      conditional: true,
      helperText: t('businessVerification.documents.boardResolution.helper'),
      file: null,
    },
    {
      key: 'financial-statements',
      label: t('businessVerification.documents.financialStatements.label'),
      required: false,
      conditional: false,
      helperText: t('businessVerification.documents.financialStatements.helper'),
      file: null,
    },
    {
      key: 'tax-certificate',
      label: t('businessVerification.documents.taxCertificate.label'),
      required: false,
      conditional: true,
      helperText: t('businessVerification.documents.taxCertificate.helper'),
      file: null,
    },
  ];
}

// ---------------------------------------------------------------------------
// Shared field classes
// ---------------------------------------------------------------------------

const INPUT_CLS =
  'block h-11 w-full rounded-lg border border-hairline-strong px-3 text-sm focus:border-[var(--nv-primary-color)] focus:ring-1 focus:ring-[var(--nv-primary-color)] focus:outline-none transition-colors';

const SELECT_CLS =
  'block h-11 w-full rounded-lg border border-hairline-strong px-3 text-sm focus:border-[var(--nv-primary-color)] focus:ring-1 focus:ring-[var(--nv-primary-color)] focus:outline-none transition-colors bg-white';

const TEXTAREA_CLS =
  'block w-full rounded-lg border border-hairline-strong px-3 py-2.5 text-sm focus:border-[var(--nv-primary-color)] focus:ring-1 focus:ring-[var(--nv-primary-color)] focus:outline-none transition-colors resize-y';

const LABEL_CLS = 'block text-sm font-medium text-ink-muted mb-1.5';

const HELPER_CLS = 'mt-1 text-xs text-ink-muted';

// ---------------------------------------------------------------------------
// Reusable inline sub-components
// ---------------------------------------------------------------------------

/** Renders a required asterisk in red. */
function RequiredMark() {
  return <span className="text-danger ms-0.5">*</span>;
}

/** Renders an "(Optional)" badge. */
function OptionalBadge() {
  return <span className="text-ink-muted font-normal ms-1">(Optional)</span>;
}

/** A subsection title within a step card. */
function Subsection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-ink mb-3 mt-2">
        {title}
      </h3>
      {children}
    </div>
  );
}

/** Grouped fields with a subtle bg. */
function FieldGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-surface-sunken rounded-lg p-4 space-y-3">{children}</div>
  );
}

function AddressFields({
  prefix,
  address,
  onChange,
}: {
  prefix: string;
  address: Address;
  onChange: (a: Address) => void;
}) {
  const { t } = useTranslation('portal');
  return (
    <div className="space-y-3">
      <div>
        <label htmlFor={`${prefix}-line1`} className={LABEL_CLS}>
          {t('businessVerification.fields.addressLine1')}<RequiredMark />
        </label>
        <input
          id={`${prefix}-line1`}
          type="text"
          value={address.line1}
          onChange={(e) => onChange({ ...address, line1: e.target.value })}
          placeholder={t('businessVerification.fields.addressLine1Placeholder')}
          className={INPUT_CLS}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={`${prefix}-city`} className={LABEL_CLS}>
            {t('businessVerification.fields.city')}<RequiredMark />
          </label>
          <input
            id={`${prefix}-city`}
            type="text"
            value={address.city}
            onChange={(e) => onChange({ ...address, city: e.target.value })}
            placeholder={t('businessVerification.fields.cityPlaceholder')}
            className={INPUT_CLS}
          />
        </div>
        <div>
          <label htmlFor={`${prefix}-state`} className={LABEL_CLS}>
            {t('businessVerification.fields.stateRegion')}<OptionalBadge />
          </label>
          <input
            id={`${prefix}-state`}
            type="text"
            value={address.state}
            onChange={(e) => onChange({ ...address, state: e.target.value })}
            placeholder={t('businessVerification.fields.statePlaceholder')}
            className={INPUT_CLS}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={`${prefix}-postal`} className={LABEL_CLS}>
            {t('businessVerification.fields.postalCode')}<RequiredMark />
          </label>
          <input
            id={`${prefix}-postal`}
            type="text"
            value={address.postalCode}
            onChange={(e) =>
              onChange({ ...address, postalCode: e.target.value })
            }
            placeholder={t('businessVerification.fields.postalCodePlaceholder')}
            className={INPUT_CLS}
          />
        </div>
        <div>
          <label htmlFor={`${prefix}-country`} className={LABEL_CLS}>
            {t('businessVerification.fields.country')}<RequiredMark />
          </label>
          <input
            id={`${prefix}-country`}
            type="text"
            value={address.country}
            onChange={(e) => onChange({ ...address, country: e.target.value })}
            placeholder={t('businessVerification.fields.countryPlaceholder')}
            className={INPUT_CLS}
          />
        </div>
      </div>
    </div>
  );
}

function formatAddressOneLine(a: Address): string {
  return [a.line1, a.city, a.state, a.postalCode, a.country]
    .filter(Boolean)
    .join(', ');
}

function isAddressValid(a: Address): boolean {
  return (
    a.line1.trim().length > 0 &&
    a.city.trim().length > 0 &&
    a.postalCode.trim().length > 0 &&
    a.country.trim().length > 0
  );
}

/** Ownership percentage visual bar for UBO step. */
function OwnershipBar({ percentage }: { percentage: number }) {
  const { t } = useTranslation('portal');
  const clamped = Math.min(percentage, 100);
  const exceeds = percentage > 100;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-ink-muted">
          {t('businessVerification.ownership.totalLabel')}
        </span>
        <span
          className={`font-semibold ${
            exceeds
              ? 'text-danger'
              : percentage > 0
                ? 'text-[var(--nv-primary-color)]'
                : 'text-ink-muted'
          }`}
        >
          {percentage.toFixed(1)}%
        </span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-surface-sunken overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            exceeds ? 'bg-danger' : 'bg-[var(--nv-primary-color)]'
          }`}
          style={{ width: `${clamped}%` }}
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('businessVerification.ownership.ariaProgress', { percentage: percentage.toFixed(1) })}
        />
      </div>
      {exceeds && (
        <p className="text-xs text-danger font-medium" role="alert">
          {t('businessVerification.ownership.exceedsWarning')}
        </p>
      )}
    </div>
  );
}

/** Collapsible card for officers and UBOs. */
function CollapsibleCard({
  title,
  defaultOpen = false,
  onRemove,
  removeLabel,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  onRemove?: () => void;
  removeLabel?: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('portal');
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border border-hairline bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-surface-sunken border-b border-hairline">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 text-sm font-medium text-ink hover:text-ink-muted transition-colors min-h-[44px]"
          aria-expanded={open}
        >
          <svg
            className={`h-4 w-4 text-ink-muted transition-transform duration-200 ${
              open ? 'rotate-90' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
          {title}
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-danger hover:text-danger transition-colors font-medium min-h-[44px] px-2"
            aria-label={removeLabel ?? t('businessVerification.actions.remove')}
          >
            {t('businessVerification.actions.remove')}
          </button>
        )}
      </div>
      {open && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface BusinessVerificationPageProps {
  branding?: {
    logoUrl: string | null;
    primaryColor: string | null;
    supportEmail: string | null;
    termsUrl: string | null;
    privacyUrl: string | null;
  };
}

export function BusinessVerificationPage({
  branding,
}: BusinessVerificationPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const { caseId } = useParams<{ caseId: string }>();
  const kybSteps = useMemo(() => getKybSteps(t), [t]);
  const entityTypes = useMemo(() => getEntityTypes(t), [t]);
  const officerRoles = useMemo(() => getOfficerRoles(t), [t]);
  const revenueRanges = useMemo(() => getRevenueRanges(t), [t]);
  const natureOfControlOptions = useMemo(() => getNatureOfControlOptions(t), [t]);
  const representativeRoles = useMemo(() => getRepresentativeRoles(t), [t]);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Business details
  const [business, setBusiness] = useState<BusinessDetails>({
    legalName: '',
    tradingName: '',
    registrationNumber: '',
    jurisdiction: '',
    entityType: 'limited_company',
    incorporationDate: '',
    taxId: '',
    lei: '',
    websiteUrl: '',
    industryCode: '',
    businessActivity: '',
    registeredAddress: emptyAddress(),
    tradingAddressSameAsRegistered: true,
    tradingAddress: emptyAddress(),
    annualRevenueRange: '',
  });

  // Step 2: Officers
  const [officers, setOfficers] = useState<OfficerEntry[]>([
    {
      key: generateKey(),
      name: '',
      role: 'director',
      nationality: '',
      dateOfBirth: '',
      residentialAddress: emptyAddress(),
      idDocument: null,
    },
  ]);

  // Step 3: UBOs (base data managed by UboDeclarationForm)
  const [ubos, setUbos] = useState<DeclaredUbo[]>([]);
  // UBO extension data keyed by UBO key
  const [uboExtensions, setUboExtensions] = useState<
    Record<string, UboExtension>
  >({});

  // Step 4: Documents
  const [docSlots, setDocSlots] = useState<DocumentSlot[]>(() => getInitialDocSlots(t));

  // Step 5: Authorized Representative
  const [representative, setRepresentative] =
    useState<AuthorizedRepresentative>({
      fullName: '',
      role: '',
      email: '',
      phone: '',
      confirmedAuthorized: false,
      authorizationEvidence: null,
    });

  // ---------------------------------------------------------------------------
  // UBO change handler -- syncs extension data
  // ---------------------------------------------------------------------------

  const handleUboChange = useCallback(
    (newUbos: DeclaredUbo[]) => {
      setUbos(newUbos);
      setUboExtensions((prev) => {
        const next: Record<string, UboExtension> = {};
        for (const u of newUbos) {
          next[u.key] = prev[u.key] ?? {
            residentialAddress: emptyAddress(),
            natureOfControl: '',
            idDocument: null,
          };
        }
        return next;
      });
    },
    [],
  );

  const updateUboExtension = useCallback(
    (key: string, patch: Partial<UboExtension>) => {
      setUboExtensions((prev) => ({
        ...prev,
        [key]: { ...prev[key], ...patch },
      }));
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  const isStep1Valid =
    business.legalName.trim().length > 0 &&
    business.registrationNumber.trim().length > 0 &&
    business.jurisdiction.trim().length > 0 &&
    business.businessActivity.trim().length > 0 &&
    isAddressValid(business.registeredAddress) &&
    (business.tradingAddressSameAsRegistered ||
      isAddressValid(business.tradingAddress));

  const isStep2Valid = officers.some(
    (o) => o.name.trim().length > 0 && o.dateOfBirth.trim().length > 0,
  );

  const uboTotalPct = ubos.reduce(
    (s, u) => s + (u.ownershipPercentage || 0),
    0,
  );
  const isStep3Valid =
    ubos.length === 0 ||
    (uboTotalPct <= 100 &&
      ubos.every(
        (u) =>
          u.name.trim().length > 0 &&
          u.ownershipPercentage > 0 &&
          uboExtensions[u.key]?.natureOfControl !== '' &&
          isAddressValid(
            uboExtensions[u.key]?.residentialAddress ?? emptyAddress(),
          ),
      ));

  const isStep4Valid = docSlots
    .filter((s) => s.required)
    .every((s) => s.file !== null);

  const isStep5Valid =
    representative.fullName.trim().length > 0 &&
    representative.role !== '' &&
    representative.email.trim().length > 0 &&
    representative.confirmedAuthorized &&
    (representative.role === 'director' ||
      representative.authorizationEvidence !== null);

  const stepValid = [
    isStep1Valid,
    isStep2Valid,
    isStep3Valid,
    isStep4Valid,
    isStep5Valid,
    true, // review step always valid
  ];

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  const handleNext = useCallback(() => {
    if (currentStep < kybSteps.length - 1) {
      setCurrentStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, kybSteps.length]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const goToStep = useCallback((step: number) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = useCallback(async () => {
    if (!caseId) return;

    setSubmitting(true);
    setError(null);

    try {
      // Map local address to API address shape
      const toAddress = (a: Address): applicant.AddressInput => ({
        line1: a.line1,
        city: a.city,
        stateOrProvince: a.state || undefined,
        postalCode: a.postalCode,
        country: a.country,
      });

      // Map revenue range form values to API enum
      const revenueMap: Record<string, applicant.AnnualRevenueRange> = {
        lt_100k: '<100k',
        '100k_1m': '100k-1m',
        '1m_10m': '1m-10m',
        '10m_100m': '10m-100m',
        gt_100m: '>100m',
      };

      const payload: applicant.KybApplicationInput = {
        business: {
          legalName: business.legalName,
          tradingName: business.tradingName || undefined,
          registrationNumber: business.registrationNumber,
          jurisdictionCountry: business.jurisdiction,
          entityType: business.entityType as applicant.KybEntityType,
          incorporationDate: business.incorporationDate,
          taxId: business.taxId || undefined,
          lei: business.lei || undefined,
          websiteUrl: business.websiteUrl || undefined,
          industrySicCode: business.industryCode || undefined,
          businessDescription: business.businessActivity,
          registeredAddress: toAddress(business.registeredAddress),
          tradingAddressSameAsRegistered:
            business.tradingAddressSameAsRegistered,
          tradingAddress: business.tradingAddressSameAsRegistered
            ? undefined
            : toAddress(business.tradingAddress),
          annualRevenueRange: revenueMap[business.annualRevenueRange] ?? undefined,
        },
        officers: officers.map((o) => ({
          fullName: o.name,
          role: o.role as applicant.KybOfficerRole,
          nationality: o.nationality,
          dateOfBirth: o.dateOfBirth,
          address: toAddress(o.residentialAddress),
          requiresIdUpload: o.idDocument !== null,
        })),
        ubos: ubos.map((u) => {
          const ext = uboExtensions[u.key];
          return {
            fullName: u.name,
            dateOfBirth: u.dateOfBirth,
            nationality: u.nationality,
            ownershipPercentage: u.ownershipPercentage,
            address: ext
              ? toAddress(ext.residentialAddress)
              : toAddress(emptyAddress()),
            natureOfControl: (ext?.natureOfControl ||
              'direct_shareholding') as applicant.KybNatureOfControl,
            requiresIdUpload: ext?.idDocument !== null && ext?.idDocument !== undefined,
          };
        }),
        representative: {
          fullName: representative.fullName,
          roleInCompany:
            representative.role as applicant.KybRepresentativeRole,
          email: representative.email,
          phoneNumber: representative.phone || undefined,
          isAuthorized: representative.confirmedAuthorized,
          requiresAuthorizationEvidence:
            representative.authorizationEvidence !== null,
        },
      };

      const client = getApiClient();
      await client.applicant.saveKybApplication(caseId, payload);

      navigate(`/case/${caseId}/confirmation`);
    } catch (err: unknown) {
      const apiErr = err as ApiClientError;
      setError(
        apiErr.message ?? t('businessVerification.errors.submitFailed'),
      );
    } finally {
      setSubmitting(false);
    }
  }, [caseId, navigate, business, officers, ubos, uboExtensions, representative, t]);

  // ---------------------------------------------------------------------------
  // Officer management
  // ---------------------------------------------------------------------------

  const addOfficer = useCallback(() => {
    setOfficers((prev) => [
      ...prev,
      {
        key: generateKey(),
        name: '',
        role: 'director',
        nationality: '',
        dateOfBirth: '',
        residentialAddress: emptyAddress(),
        idDocument: null,
      },
    ]);
  }, []);

  const removeOfficer = useCallback((key: string) => {
    setOfficers((prev) => prev.filter((o) => o.key !== key));
  }, []);

  const updateOfficer = useCallback(
    (key: string, field: keyof OfficerEntry, value: string | File | null | Address) => {
      setOfficers((prev) =>
        prev.map((o) => (o.key === key ? { ...o, [field]: value } : o)),
      );
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Document management
  // ---------------------------------------------------------------------------

  const updateDocFile = useCallback((slotKey: string, file: File | null) => {
    setDocSlots((prev) =>
      prev.map((s) => (s.key === slotKey ? { ...s, file } : s)),
    );
  }, []);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const currentStepKey = kybSteps[currentStep].key;
  const meta = getStepMeta(t, currentStepKey);

  const requiredDocs = docSlots.filter((s) => s.required);
  const optionalDocs = docSlots.filter((s) => !s.required);

  // Check review completeness for highlighting missing fields
  const reviewMissing = {
    legalName: !business.legalName.trim(),
    registrationNumber: !business.registrationNumber.trim(),
    jurisdiction: !business.jurisdiction.trim(),
    businessActivity: !business.businessActivity.trim(),
    registeredAddress: !isAddressValid(business.registeredAddress),
    officers: !isStep2Valid,
    requiredDocs: !isStep4Valid,
    repName: !representative.fullName.trim(),
    repRole: representative.role === '',
    repEmail: !representative.email.trim(),
    repAuthorized: !representative.confirmedAuthorized,
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      {/* ================================================================= */}
      {/* Header                                                            */}
      {/* ================================================================= */}
      <header className="sticky top-0 z-10 border-b border-hairline bg-surface">
        <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 py-4 flex items-center gap-3">
          {branding?.logoUrl && (
            <img
              src={branding.logoUrl}
              alt={t('businessVerification.logoAlt')}
              className="h-8 shrink-0"
            />
          )}
          <h1
            className="text-lg font-semibold truncate"
            style={{ color: 'var(--nv-primary-color)' }}
          >
            {t('businessVerification.headerTitle')}
          </h1>
        </div>
      </header>

      {/* ================================================================= */}
      {/* Step indicator                                                    */}
      {/* ================================================================= */}
      <div className="bg-white border-b border-hairline">
        <div className="mx-auto w-full max-w-2xl px-4 sm:px-6">
          {/* Desktop step indicator */}
          <div className="hidden sm:block">
            <StepIndicator currentStep={currentStepKey} steps={kybSteps} />
          </div>
          {/* Mobile compact step indicator */}
          <div className="sm:hidden py-3">
            <p className="text-center text-sm font-medium text-ink-muted">
              {t('stepIndicator.stepCount', { current: currentStep + 1, total: kybSteps.length })}
            </p>
            {/* Progress bar */}
            <div className="mt-2 h-1.5 w-full rounded-full bg-surface-sunken overflow-hidden">
              <div
                className="h-full rounded-full bg-[var(--nv-primary-color)] transition-all duration-300"
                style={{
                  width: `${((currentStep + 1) / kybSteps.length) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* Main content                                                      */}
      {/* ================================================================= */}
      <main className="flex-1">
        <div className="mx-auto w-full max-w-2xl px-4 sm:px-6 py-6">
          {/* Step header */}
          <div className="flex items-start gap-3 mb-6">
            <div
              className="shrink-0 mt-0.5 text-[var(--nv-primary-color)]"
              aria-hidden="true"
            >
              {meta.icon}
            </div>
            <div>
              <h2 className="text-xl font-bold text-ink">{meta.title}</h2>
              <p className="mt-1 text-sm text-ink-muted">{meta.subtitle}</p>
            </div>
          </div>

          {/* Step content card */}
          <div className="rounded-lg bg-white p-6 md:p-8 border border-hairline">
            {/* =========================================================== */}
            {/* Step 1: Business Details                                     */}
            {/* =========================================================== */}
            {currentStep === 0 && (
              <div className="space-y-8">
                {/* Company Information */}
                <Subsection title={t('businessVerification.subsections.companyInformation')}>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="legal-name" className={LABEL_CLS}>
                        {t('businessVerification.fields.legalName')}<RequiredMark />
                      </label>
                      <input
                        id="legal-name"
                        type="text"
                        value={business.legalName}
                        onChange={(e) =>
                          setBusiness({ ...business, legalName: e.target.value })
                        }
                        placeholder={t('businessVerification.fields.legalNamePlaceholder')}
                        className={INPUT_CLS}
                      />
                    </div>

                    <div>
                      <label htmlFor="trading-name" className={LABEL_CLS}>
                        {t('businessVerification.fields.tradingNameDba')}<OptionalBadge />
                      </label>
                      <input
                        id="trading-name"
                        type="text"
                        value={business.tradingName}
                        onChange={(e) =>
                          setBusiness({
                            ...business,
                            tradingName: e.target.value,
                          })
                        }
                        placeholder={t('businessVerification.fields.tradingNamePlaceholder')}
                        className={INPUT_CLS}
                      />
                      <p className={HELPER_CLS}>
                        {t('businessVerification.fields.tradingNameHelper')}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="reg-number" className={LABEL_CLS}>
                          {t('businessVerification.fields.registrationNumber')}<RequiredMark />
                        </label>
                        <input
                          id="reg-number"
                          type="text"
                          value={business.registrationNumber}
                          onChange={(e) =>
                            setBusiness({
                              ...business,
                              registrationNumber: e.target.value,
                            })
                          }
                          placeholder={t('businessVerification.fields.registrationNumberPlaceholder')}
                          className={INPUT_CLS}
                        />
                      </div>
                      <div>
                        <label htmlFor="jurisdiction" className={LABEL_CLS}>
                          {t('businessVerification.fields.jurisdiction')}<RequiredMark />
                        </label>
                        <input
                          id="jurisdiction"
                          type="text"
                          value={business.jurisdiction}
                          onChange={(e) =>
                            setBusiness({
                              ...business,
                              jurisdiction: e.target.value,
                            })
                          }
                          placeholder={t('businessVerification.fields.jurisdictionShortPlaceholder')}
                          className={INPUT_CLS}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="entity-type" className={LABEL_CLS}>
                          {t('businessVerification.fields.entityType')}
                        </label>
                        <select
                          id="entity-type"
                          value={business.entityType}
                          onChange={(e) =>
                            setBusiness({
                              ...business,
                              entityType: e.target.value,
                            })
                          }
                          className={SELECT_CLS}
                        >
                          {entityTypes.map((et) => (
                            <option key={et.value} value={et.value}>
                              {et.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="inc-date" className={LABEL_CLS}>
                          {t('businessVerification.fields.incorporationDate')}<OptionalBadge />
                        </label>
                        <input
                          id="inc-date"
                          type="date"
                          value={business.incorporationDate}
                          onChange={(e) =>
                            setBusiness({
                              ...business,
                              incorporationDate: e.target.value,
                            })
                          }
                          className={INPUT_CLS}
                        />
                      </div>
                    </div>
                  </div>
                </Subsection>

                {/* Tax & Registration */}
                <Subsection title={t('businessVerification.subsections.taxRegistration')}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="tax-id" className={LABEL_CLS}>
                        {t('businessVerification.fields.taxIdVat')}<OptionalBadge />
                      </label>
                      <input
                        id="tax-id"
                        type="text"
                        value={business.taxId}
                        onChange={(e) =>
                          setBusiness({ ...business, taxId: e.target.value })
                        }
                        placeholder={t('businessVerification.fields.taxIdPlaceholder')}
                        className={INPUT_CLS}
                      />
                      <p className={HELPER_CLS}>
                        {t('businessVerification.fields.taxIdHelper')}
                      </p>
                    </div>
                    <div>
                      <label htmlFor="lei" className={LABEL_CLS}>
                        {t('businessVerification.fields.leiShort')}<OptionalBadge />
                      </label>
                      <input
                        id="lei"
                        type="text"
                        value={business.lei}
                        onChange={(e) =>
                          setBusiness({ ...business, lei: e.target.value })
                        }
                        placeholder={t('businessVerification.fields.leiPlaceholder')}
                        maxLength={20}
                        className={INPUT_CLS}
                      />
                      <p className={HELPER_CLS}>
                        {t('businessVerification.fields.leiHelper')}
                      </p>
                    </div>
                  </div>
                </Subsection>

                {/* Addresses */}
                <Subsection title={t('businessVerification.subsections.addresses')}>
                  <div className="space-y-5">
                    <FieldGroup>
                      <p className="text-sm font-semibold text-ink">
                        {t('businessVerification.fields.registeredAddressLabel')}<RequiredMark />
                      </p>
                      <AddressFields
                        prefix="reg-addr"
                        address={business.registeredAddress}
                        onChange={(a) =>
                          setBusiness({ ...business, registeredAddress: a })
                        }
                      />
                    </FieldGroup>

                    <FieldGroup>
                      <p className="text-sm font-semibold text-ink">
                        {t('businessVerification.fields.tradingAddressOperational')}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          id="trading-same"
                          type="checkbox"
                          checked={business.tradingAddressSameAsRegistered}
                          onChange={(e) =>
                            setBusiness({
                              ...business,
                              tradingAddressSameAsRegistered: e.target.checked,
                            })
                          }
                          className="h-4 w-4 rounded border-hairline-strong text-[var(--nv-primary-color)] focus:ring-[var(--nv-primary-color)]"
                        />
                        <label
                          htmlFor="trading-same"
                          className="text-sm text-ink-muted"
                        >
                          {t('businessVerification.fields.tradingAddressSameShort')}
                        </label>
                      </div>
                      {!business.tradingAddressSameAsRegistered && (
                        <div className="mt-3 transition-all">
                          <AddressFields
                            prefix="trading-addr"
                            address={business.tradingAddress}
                            onChange={(a) =>
                              setBusiness({ ...business, tradingAddress: a })
                            }
                          />
                        </div>
                      )}
                    </FieldGroup>
                  </div>
                </Subsection>

                {/* Additional */}
                <Subsection title={t('businessVerification.subsections.additionalInformation')}>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="website-url" className={LABEL_CLS}>
                          {t('businessVerification.fields.websiteUrl')}<OptionalBadge />
                        </label>
                        <input
                          id="website-url"
                          type="url"
                          value={business.websiteUrl}
                          onChange={(e) =>
                            setBusiness({
                              ...business,
                              websiteUrl: e.target.value,
                            })
                          }
                          placeholder={t('businessVerification.fields.websitePlaceholderExample')}
                          className={INPUT_CLS}
                        />
                      </div>
                      <div>
                        <label htmlFor="industry-code" className={LABEL_CLS}>
                          {t('businessVerification.fields.industrySicCode')}<OptionalBadge />
                        </label>
                        <input
                          id="industry-code"
                          type="text"
                          value={business.industryCode}
                          onChange={(e) =>
                            setBusiness({
                              ...business,
                              industryCode: e.target.value,
                            })
                          }
                          placeholder={t('businessVerification.fields.industryCodePlaceholder')}
                          className={INPUT_CLS}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="business-activity" className={LABEL_CLS}>
                        {t('businessVerification.fields.businessActivityFull')}<RequiredMark />
                      </label>
                      <textarea
                        id="business-activity"
                        value={business.businessActivity}
                        onChange={(e) =>
                          setBusiness({
                            ...business,
                            businessActivity: e.target.value,
                          })
                        }
                        rows={3}
                        placeholder={t('businessVerification.fields.businessActivityFullPlaceholder')}
                        className={TEXTAREA_CLS}
                      />
                    </div>

                    <div>
                      <label htmlFor="annual-revenue" className={LABEL_CLS}>
                        {t('businessVerification.fields.annualRevenue')}<OptionalBadge />
                      </label>
                      <select
                        id="annual-revenue"
                        value={business.annualRevenueRange}
                        onChange={(e) =>
                          setBusiness({
                            ...business,
                            annualRevenueRange: e.target.value,
                          })
                        }
                        className={SELECT_CLS}
                      >
                        {revenueRanges.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </Subsection>
              </div>
            )}

            {/* =========================================================== */}
            {/* Step 2: Officers                                             */}
            {/* =========================================================== */}
            {currentStep === 1 && (
              <div className="space-y-5">
                <div
                  className="rounded-md border border-info/30 bg-info-tint px-4 py-3 text-sm text-info"
                  role="note"
                >
                  {t('businessVerification.notes.officersDirectors')}
                </div>

                {officers.map((officer, idx) => (
                  <CollapsibleCard
                    key={officer.key}
                    title={
                      officer.name.trim()
                        ? t('businessVerification.officerCard.directorTitleWithName', {
                            number: idx + 1,
                            name: officer.name,
                          })
                        : t('businessVerification.officerCard.directorTitle', {
                            number: idx + 1,
                          })
                    }
                    defaultOpen={idx === 0}
                    onRemove={
                      officers.length > 1
                        ? () => removeOfficer(officer.key)
                        : undefined
                    }
                    removeLabel={t('businessVerification.officerCard.removeAria', { number: idx + 1 })}
                  >
                    <FieldGroup>
                      {/* Name + Role */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                          <label
                            htmlFor={`officer-name-${officer.key}`}
                            className={LABEL_CLS}
                          >
                            {t('businessVerification.fields.officerName')}<RequiredMark />
                          </label>
                          <input
                            id={`officer-name-${officer.key}`}
                            type="text"
                            value={officer.name}
                            onChange={(e) =>
                              updateOfficer(
                                officer.key,
                                'name',
                                e.target.value,
                              )
                            }
                            placeholder={t('businessVerification.fields.officerNamePlaceholder')}
                            className={INPUT_CLS}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor={`officer-role-${officer.key}`}
                            className={LABEL_CLS}
                          >
                            {t('businessVerification.fields.officerRole')}
                          </label>
                          <select
                            id={`officer-role-${officer.key}`}
                            value={officer.role}
                            onChange={(e) =>
                              updateOfficer(
                                officer.key,
                                'role',
                                e.target.value,
                              )
                            }
                            className={SELECT_CLS}
                          >
                            {officerRoles.map((r) => (
                              <option key={r.value} value={r.value}>
                                {r.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Nationality + DOB */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label
                            htmlFor={`officer-nationality-${officer.key}`}
                            className={LABEL_CLS}
                          >
                            {t('businessVerification.fields.officerNationality')}<OptionalBadge />
                          </label>
                          <input
                            id={`officer-nationality-${officer.key}`}
                            type="text"
                            value={officer.nationality}
                            onChange={(e) =>
                              updateOfficer(
                                officer.key,
                                'nationality',
                                e.target.value,
                              )
                            }
                            placeholder={t('businessVerification.fields.officerNationalityPlaceholder')}
                            className={INPUT_CLS}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor={`officer-dob-${officer.key}`}
                            className={LABEL_CLS}
                          >
                            {t('businessVerification.fields.officerDateOfBirth')}<RequiredMark />
                          </label>
                          <input
                            id={`officer-dob-${officer.key}`}
                            type="date"
                            value={officer.dateOfBirth}
                            onChange={(e) =>
                              updateOfficer(
                                officer.key,
                                'dateOfBirth',
                                e.target.value,
                              )
                            }
                            className={INPUT_CLS}
                          />
                        </div>
                      </div>
                    </FieldGroup>

                    {/* Residential Address */}
                    <div>
                      <p className={LABEL_CLS}>
                        {t('businessVerification.fields.officerAddress')}<OptionalBadge />
                      </p>
                      <FieldGroup>
                        <AddressFields
                          prefix={`officer-addr-${officer.key}`}
                          address={officer.residentialAddress}
                          onChange={(a) =>
                            updateOfficer(
                              officer.key,
                              'residentialAddress',
                              a,
                            )
                          }
                        />
                      </FieldGroup>
                    </div>

                    {/* ID Document Upload */}
                    <div>
                      <FileUpload
                        id={`officer-id-doc-${officer.key}`}
                        label={t('businessVerification.fields.officerIdDocumentOptional')}
                        file={officer.idDocument}
                        onFileChange={(f) =>
                          updateOfficer(officer.key, 'idDocument', f)
                        }
                      />
                      <p className={HELPER_CLS}>
                        {t('businessVerification.fields.officerIdHelper')}
                      </p>
                    </div>
                  </CollapsibleCard>
                ))}

                <button
                  type="button"
                  onClick={addOfficer}
                  className="inline-flex items-center gap-2 rounded-lg border-2 border-dashed border-hairline-strong px-4 py-3 text-sm font-medium text-ink-muted hover:border-hairline-strong hover:text-ink-muted transition-colors w-full justify-center min-h-[44px]"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  {t('businessVerification.officerCard.addAnother')}
                </button>
              </div>
            )}

            {/* =========================================================== */}
            {/* Step 3: UBOs                                                 */}
            {/* =========================================================== */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div
                  className="rounded-md border border-info/30 bg-info-tint px-4 py-3 text-sm text-info"
                  role="note"
                >
                  <strong>{t('businessVerification.notes.uboRegulatoryLabel')}</strong>{' '}
                  {t('businessVerification.notes.uboRegulatoryText')}
                </div>

                {/* Ownership bar */}
                <OwnershipBar percentage={uboTotalPct} />

                <UboDeclarationForm
                  ubos={ubos}
                  onChange={handleUboChange}
                  threshold={25}
                />

                {/* UBO Extension fields for each declared UBO */}
                {ubos.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-ink">
                      {t('businessVerification.subsections.additionalUboDetails')}
                    </h3>
                    {ubos.map((ubo, idx) => {
                      const ext = uboExtensions[ubo.key] ?? {
                        residentialAddress: emptyAddress(),
                        natureOfControl: '',
                        idDocument: null,
                      };
                      return (
                        <CollapsibleCard
                          key={ubo.key}
                          title={
                            ubo.name.trim()
                              ? t('businessVerification.uboCard.detailsSuffix', { name: ubo.name })
                              : t('businessVerification.uboCard.detailsTitle', { number: idx + 1 })
                          }
                          defaultOpen={idx === 0}
                        >
                          {/* Nature of Control */}
                          <div>
                            <label
                              htmlFor={`ubo-control-${ubo.key}`}
                              className={LABEL_CLS}
                            >
                              {t('businessVerification.fields.uboNatureOfControl')}<RequiredMark />
                            </label>
                            <select
                              id={`ubo-control-${ubo.key}`}
                              value={ext.natureOfControl}
                              onChange={(e) =>
                                updateUboExtension(ubo.key, {
                                  natureOfControl: e.target.value,
                                })
                              }
                              className={SELECT_CLS}
                            >
                              {natureOfControlOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Residential Address */}
                          <div>
                            <p className={LABEL_CLS}>
                              {t('businessVerification.fields.uboAddress')}<RequiredMark />
                            </p>
                            <FieldGroup>
                              <AddressFields
                                prefix={`ubo-addr-${ubo.key}`}
                                address={ext.residentialAddress}
                                onChange={(a) =>
                                  updateUboExtension(ubo.key, {
                                    residentialAddress: a,
                                  })
                                }
                              />
                            </FieldGroup>
                          </div>

                          {/* ID Document Upload */}
                          <div>
                            <FileUpload
                              id={`ubo-id-doc-${ubo.key}`}
                              label={t('businessVerification.fields.uboIdDocumentOptional')}
                              file={ext.idDocument}
                              onFileChange={(f) =>
                                updateUboExtension(ubo.key, {
                                  idDocument: f,
                                })
                              }
                            />
                          </div>
                        </CollapsibleCard>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* =========================================================== */}
            {/* Step 4: Documents                                            */}
            {/* =========================================================== */}
            {currentStep === 3 && (
              <div className="space-y-6">
                {/* Required documents */}
                <div className="space-y-5">
                  <h3 className="text-sm font-semibold text-ink">
                    {t('businessVerification.subsections.requiredDocuments')}
                  </h3>
                  {requiredDocs.map((slot) => (
                    <div
                      key={slot.key}
                      className="flex items-start gap-3 rounded-lg border border-hairline p-4"
                    >
                      {/* Status indicator */}
                      <div className="shrink-0 mt-0.5">
                        {slot.file ? (
                          <svg
                            className="h-5 w-5 text-primary"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        ) : (
                          <div
                            className="h-5 w-5 rounded border-2 border-hairline-strong"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <FileUpload
                          id={`doc-${slot.key}`}
                          label={`${slot.label}${t('uploadDocuments.slots.requiredSuffix')}`}
                          file={slot.file}
                          onFileChange={(f) => updateDocFile(slot.key, f)}
                        />
                        {slot.helperText && (
                          <p className={HELPER_CLS}>{slot.helperText}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Visual separator */}
                {optionalDocs.length > 0 && (
                  <>
                    <div className="relative">
                      <div
                        className="absolute inset-0 flex items-center"
                        aria-hidden="true"
                      >
                        <div className="w-full border-t border-hairline" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-white px-3 text-xs text-ink-muted uppercase tracking-wide">
                          {t('businessVerification.subsections.optionalDocuments')}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-5">
                      {optionalDocs.map((slot) => (
                        <div
                          key={slot.key}
                          className="flex items-start gap-3 rounded-lg border border-hairline p-4"
                        >
                          {/* Status indicator */}
                          <div className="shrink-0 mt-0.5">
                            {slot.file ? (
                              <svg
                                className="h-5 w-5 text-primary"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            ) : (
                              <div
                                className="h-5 w-5 rounded border-2 border-hairline-strong"
                                aria-hidden="true"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <FileUpload
                              id={`doc-${slot.key}`}
                              label={`${slot.label}${slot.conditional ? '' : t('businessVerification.documents.optionalSuffix')}`}
                              file={slot.file}
                              onFileChange={(f) => updateDocFile(slot.key, f)}
                            />
                            {slot.helperText && (
                              <p className={HELPER_CLS}>{slot.helperText}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* =========================================================== */}
            {/* Step 5: Authorized Representative                            */}
            {/* =========================================================== */}
            {currentStep === 4 && (
              <div className="space-y-5">
                <div className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <label htmlFor="rep-name" className={LABEL_CLS}>
                      {t('businessVerification.fields.repFullName')}<RequiredMark />
                    </label>
                    <input
                      id="rep-name"
                      type="text"
                      value={representative.fullName}
                      onChange={(e) =>
                        setRepresentative({
                          ...representative,
                          fullName: e.target.value,
                        })
                      }
                      placeholder={t('businessVerification.fields.repFullNamePlaceholder')}
                      className={INPUT_CLS}
                    />
                  </div>

                  {/* Role */}
                  <div>
                    <label htmlFor="rep-role" className={LABEL_CLS}>
                      {t('businessVerification.fields.repRole')}<RequiredMark />
                    </label>
                    <select
                      id="rep-role"
                      value={representative.role}
                      onChange={(e) =>
                        setRepresentative({
                          ...representative,
                          role: e.target.value,
                        })
                      }
                      className={SELECT_CLS}
                    >
                      {representativeRoles.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Email + Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="rep-email" className={LABEL_CLS}>
                        {t('businessVerification.fields.repEmail')}<RequiredMark />
                      </label>
                      <input
                        id="rep-email"
                        type="email"
                        value={representative.email}
                        onChange={(e) =>
                          setRepresentative({
                            ...representative,
                            email: e.target.value,
                          })
                        }
                        placeholder={t('businessVerification.fields.repEmailPlaceholder')}
                        className={INPUT_CLS}
                      />
                    </div>
                    <div>
                      <label htmlFor="rep-phone" className={LABEL_CLS}>
                        {t('businessVerification.fields.repPhoneShort')}<OptionalBadge />
                      </label>
                      <input
                        id="rep-phone"
                        type="tel"
                        value={representative.phone}
                        onChange={(e) =>
                          setRepresentative({
                            ...representative,
                            phone: e.target.value,
                          })
                        }
                        placeholder={t('businessVerification.fields.repPhonePlaceholder')}
                        className={INPUT_CLS}
                      />
                    </div>
                  </div>

                  {/* Confirmation Checkbox */}
                  <div
                    className={`flex items-start gap-3 rounded-lg p-4 border transition-colors ${
                      representative.confirmedAuthorized
                        ? 'border-primary/30 bg-primary-tint'
                        : 'border-hairline bg-surface-sunken'
                    }`}
                  >
                    <input
                      id="rep-confirm"
                      type="checkbox"
                      checked={representative.confirmedAuthorized}
                      onChange={(e) =>
                        setRepresentative({
                          ...representative,
                          confirmedAuthorized: e.target.checked,
                        })
                      }
                      className="mt-0.5 h-5 w-5 rounded border-hairline-strong text-[var(--nv-primary-color)] focus:ring-[var(--nv-primary-color)]"
                    />
                    <label
                      htmlFor="rep-confirm"
                      className="text-sm text-ink-muted leading-relaxed"
                    >
                      {t('businessVerification.fields.repAuthorizationConfirm')}{' '}
                      <RequiredMark />
                    </label>
                  </div>

                  {/* Authorization Evidence (if not a director) */}
                  {representative.role !== '' &&
                    representative.role !== 'director' && (
                      <div className="rounded-lg border border-warning/30 bg-warning-tint p-4 space-y-3">
                        <p className="text-sm text-warning">
                          {t('businessVerification.notes.repNotDirector')}
                        </p>
                        <FileUpload
                          id="rep-auth-evidence"
                          label={t('businessVerification.fields.repAuthorizationEvidenceRequired')}
                          file={representative.authorizationEvidence}
                          onFileChange={(f) =>
                            setRepresentative({
                              ...representative,
                              authorizationEvidence: f,
                            })
                          }
                        />
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* =========================================================== */}
            {/* Step 6: Review & Submit                                      */}
            {/* =========================================================== */}
            {currentStep === 5 && (
              <div className="space-y-6">
                {/* ---- Business Details ---- */}
                <section className="rounded-lg border border-hairline overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-surface-sunken border-b border-hairline">
                    <h3 className="text-sm font-semibold text-ink">
                      {t('businessVerification.reviewLabels.businessDetails')}
                    </h3>
                    <button
                      type="button"
                      onClick={() => goToStep(0)}
                      className="text-xs font-medium text-[var(--nv-primary-color)] hover:opacity-80 transition-opacity min-h-[44px] px-2 flex items-center"
                    >
                      {t('businessVerification.actions.edit')}
                    </button>
                  </div>
                  <div className="p-4">
                    <dl className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
                      <dt
                        className={`text-ink-muted ${reviewMissing.legalName ? 'text-danger' : ''}`}
                      >
                        {t('businessVerification.reviewLabels.legalName')}
                      </dt>
                      <dd
                        className={`text-ink ${reviewMissing.legalName ? 'text-danger font-medium' : ''}`}
                      >
                        {business.legalName || t('businessVerification.reviewLabels.missing')}
                      </dd>

                      {business.tradingName && (
                        <>
                          <dt className="text-ink-muted">{t('businessVerification.reviewLabels.tradingName')}</dt>
                          <dd className="text-ink">
                            {business.tradingName}
                          </dd>
                        </>
                      )}

                      <dt
                        className={`text-ink-muted ${reviewMissing.registrationNumber ? 'text-danger' : ''}`}
                      >
                        {t('businessVerification.reviewLabels.registration')}
                      </dt>
                      <dd
                        className={`text-ink ${reviewMissing.registrationNumber ? 'text-danger font-medium' : ''}`}
                      >
                        {business.registrationNumber || t('businessVerification.reviewLabels.missing')}
                      </dd>

                      <dt
                        className={`text-ink-muted ${reviewMissing.jurisdiction ? 'text-danger' : ''}`}
                      >
                        {t('businessVerification.reviewLabels.jurisdiction')}
                      </dt>
                      <dd
                        className={`text-ink ${reviewMissing.jurisdiction ? 'text-danger font-medium' : ''}`}
                      >
                        {business.jurisdiction || t('businessVerification.reviewLabels.missing')}
                      </dd>

                      <dt className="text-ink-muted">{t('businessVerification.reviewLabels.type')}</dt>
                      <dd className="text-ink">
                        {entityTypes.find(
                          (e) => e.value === business.entityType,
                        )?.label ?? business.entityType}
                      </dd>

                      {business.incorporationDate && (
                        <>
                          <dt className="text-ink-muted">
                            {t('businessVerification.reviewLabels.incorporationDate')}
                          </dt>
                          <dd className="text-ink">
                            {business.incorporationDate}
                          </dd>
                        </>
                      )}

                      {business.taxId && (
                        <>
                          <dt className="text-ink-muted">{t('businessVerification.reviewLabels.taxIdVat')}</dt>
                          <dd className="text-ink">{business.taxId}</dd>
                        </>
                      )}

                      {business.lei && (
                        <>
                          <dt className="text-ink-muted">{t('businessVerification.reviewLabels.lei')}</dt>
                          <dd className="text-ink">{business.lei}</dd>
                        </>
                      )}

                      {business.websiteUrl && (
                        <>
                          <dt className="text-ink-muted">{t('businessVerification.reviewLabels.website')}</dt>
                          <dd className="text-ink">
                            {business.websiteUrl}
                          </dd>
                        </>
                      )}

                      {business.industryCode && (
                        <>
                          <dt className="text-ink-muted">{t('businessVerification.reviewLabels.industrySic')}</dt>
                          <dd className="text-ink">
                            {business.industryCode}
                          </dd>
                        </>
                      )}

                      <dt
                        className={`text-ink-muted ${reviewMissing.businessActivity ? 'text-danger' : ''}`}
                      >
                        {t('businessVerification.reviewLabels.businessActivity')}
                      </dt>
                      <dd
                        className={`text-ink sm:col-span-1 ${reviewMissing.businessActivity ? 'text-danger font-medium' : ''}`}
                      >
                        {business.businessActivity || t('businessVerification.reviewLabels.missing')}
                      </dd>

                      {business.annualRevenueRange && (
                        <>
                          <dt className="text-ink-muted">{t('businessVerification.reviewLabels.annualRevenue')}</dt>
                          <dd className="text-ink">
                            {revenueRanges.find(
                              (r) => r.value === business.annualRevenueRange,
                            )?.label ?? business.annualRevenueRange}
                          </dd>
                        </>
                      )}
                    </dl>

                    {/* Addresses */}
                    <div className="mt-4 pt-4 border-t border-hairline space-y-3">
                      <div>
                        <p
                          className={`text-xs font-semibold mb-1 ${reviewMissing.registeredAddress ? 'text-danger' : 'text-ink-muted'}`}
                        >
                          {t('businessVerification.reviewLabels.registeredAddress')}
                        </p>
                        <p
                          className={`text-sm ${reviewMissing.registeredAddress ? 'text-danger font-medium' : 'text-ink'}`}
                        >
                          {formatAddressOneLine(business.registeredAddress) ||
                            t('businessVerification.reviewLabels.missing')}
                        </p>
                      </div>
                      {!business.tradingAddressSameAsRegistered && (
                        <div>
                          <p className="text-xs font-semibold text-ink-muted mb-1">
                            {t('businessVerification.reviewLabels.tradingAddress')}
                          </p>
                          <p className="text-sm text-ink">
                            {formatAddressOneLine(business.tradingAddress) ||
                              '-'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                {/* ---- Officers ---- */}
                <section className="rounded-lg border border-hairline overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-surface-sunken border-b border-hairline">
                    <h3 className="text-sm font-semibold text-ink">
                      {t('businessVerification.reviewLabels.officersHeading', {
                        count: officers.filter((o) => o.name.trim()).length,
                      })}
                    </h3>
                    <button
                      type="button"
                      onClick={() => goToStep(1)}
                      className="text-xs font-medium text-[var(--nv-primary-color)] hover:opacity-80 transition-opacity min-h-[44px] px-2 flex items-center"
                    >
                      {t('businessVerification.actions.edit')}
                    </button>
                  </div>
                  <div className="p-4">
                    {officers.filter((o) => o.name.trim()).length === 0 ? (
                      <p
                        className={`text-sm ${reviewMissing.officers ? 'text-danger font-medium' : 'text-ink-muted'}`}
                      >
                        {reviewMissing.officers
                          ? t('businessVerification.reviewLabels.officersRequired')
                          : t('businessVerification.reviewLabels.noOfficersListed')}
                      </p>
                    ) : (
                      <ul className="text-sm text-ink-muted space-y-3">
                        {officers
                          .filter((o) => o.name.trim())
                          .map((o) => (
                            <li
                              key={o.key}
                              className="rounded-lg border border-hairline bg-surface-sunken px-4 py-3"
                            >
                              <p className="font-medium text-ink">
                                {o.name}
                              </p>
                              <p className="text-xs text-ink-muted mt-0.5">
                                {officerRoles.find(
                                  (r) => r.value === o.role,
                                )?.label ?? o.role}
                                {o.dateOfBirth && ` | ${t('businessVerification.reviewLabels.dobPrefix')} ${o.dateOfBirth}`}
                                {o.nationality && ` | ${o.nationality}`}
                              </p>
                              {isAddressValid(o.residentialAddress) && (
                                <p className="text-xs text-ink-muted mt-1">
                                  {t('businessVerification.reviewLabels.addressPrefix')}{' '}
                                  {formatAddressOneLine(o.residentialAddress)}
                                </p>
                              )}
                              {o.idDocument && (
                                <p className="text-xs text-primary mt-1">
                                  {t('businessVerification.reviewLabels.idDocumentPrefix')} {o.idDocument.name}
                                </p>
                              )}
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                </section>

                {/* ---- UBOs ---- */}
                <section className="rounded-lg border border-hairline overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-surface-sunken border-b border-hairline">
                    <h3 className="text-sm font-semibold text-ink">
                      {t('businessVerification.reviewLabels.ubosHeading', {
                        count: ubos.filter((u) => u.name.trim()).length,
                      })}
                    </h3>
                    <button
                      type="button"
                      onClick={() => goToStep(2)}
                      className="text-xs font-medium text-[var(--nv-primary-color)] hover:opacity-80 transition-opacity min-h-[44px] px-2 flex items-center"
                    >
                      {t('businessVerification.actions.edit')}
                    </button>
                  </div>
                  <div className="p-4">
                    {ubos.filter((u) => u.name.trim()).length === 0 ? (
                      <p className="text-sm text-ink-muted">
                        {t('businessVerification.reviewLabels.noUbosDeclared')}
                      </p>
                    ) : (
                      <ul className="text-sm text-ink-muted space-y-3">
                        {ubos
                          .filter((u) => u.name.trim())
                          .map((u) => {
                            const ext = uboExtensions[u.key];
                            return (
                              <li
                                key={u.key}
                                className="rounded-lg border border-hairline bg-surface-sunken px-4 py-3"
                              >
                                <p className="font-medium text-ink">
                                  {u.name}{' '}
                                  <span className="font-normal text-ink-muted">
                                    -- {u.ownershipPercentage}%
                                  </span>
                                </p>
                                <p className="text-xs text-ink-muted mt-0.5">
                                  {u.dateOfBirth && `${t('businessVerification.reviewLabels.dobPrefix')} ${u.dateOfBirth}`}
                                  {u.nationality &&
                                    `${u.dateOfBirth ? ' | ' : ''}${u.nationality}`}
                                  {ext?.natureOfControl &&
                                    `${u.dateOfBirth || u.nationality ? ' | ' : ''}${t('businessVerification.reviewLabels.controlPrefix')} ${
                                      natureOfControlOptions.find(
                                        (o) =>
                                          o.value === ext.natureOfControl,
                                      )?.label ?? ext.natureOfControl
                                    }`}
                                </p>
                                {ext &&
                                  isAddressValid(ext.residentialAddress) && (
                                    <p className="text-xs text-ink-muted mt-1">
                                      {t('businessVerification.reviewLabels.addressPrefix')}{' '}
                                      {formatAddressOneLine(
                                        ext.residentialAddress,
                                      )}
                                    </p>
                                  )}
                                {ext?.idDocument && (
                                  <p className="text-xs text-primary mt-1">
                                    {t('businessVerification.reviewLabels.idDocumentPrefix')} {ext.idDocument.name}
                                  </p>
                                )}
                              </li>
                            );
                          })}
                      </ul>
                    )}
                  </div>
                </section>

                {/* ---- Documents ---- */}
                <section className="rounded-lg border border-hairline overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-surface-sunken border-b border-hairline">
                    <h3 className="text-sm font-semibold text-ink">
                      {t('businessVerification.reviewLabels.documentsHeading', {
                        uploaded: docSlots.filter((s) => s.file).length,
                        total: docSlots.length,
                      })}
                    </h3>
                    <button
                      type="button"
                      onClick={() => goToStep(3)}
                      className="text-xs font-medium text-[var(--nv-primary-color)] hover:opacity-80 transition-opacity min-h-[44px] px-2 flex items-center"
                    >
                      {t('businessVerification.actions.edit')}
                    </button>
                  </div>
                  <div className="p-4">
                    <ul className="text-sm space-y-2">
                      {docSlots.map((s) => {
                        const isMissing = s.required && !s.file;
                        return (
                          <li
                            key={s.key}
                            className="flex items-center gap-2"
                          >
                            {s.file ? (
                              <svg
                                className="h-4 w-4 shrink-0 text-primary"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            ) : (
                              <div
                                className={`h-4 w-4 shrink-0 rounded border-2 ${isMissing ? 'border-danger/40' : 'border-hairline-strong'}`}
                                aria-hidden="true"
                              />
                            )}
                            <span
                              className={
                                s.file
                                  ? 'text-ink'
                                  : isMissing
                                    ? 'text-danger font-medium'
                                    : 'text-ink-muted'
                              }
                            >
                              {s.label}
                              {s.required ? t('uploadDocuments.slots.requiredSuffix') : ''}
                              {s.file ? (
                                <span className="ms-1 text-ink-muted font-normal">
                                  -- {s.file.name}
                                </span>
                              ) : isMissing ? (
                                <span className="ms-1 text-danger font-normal">
                                  {t('businessVerification.reviewLabels.notUploaded')}
                                </span>
                              ) : null}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </section>

                {/* ---- Authorized Representative ---- */}
                <section className="rounded-lg border border-hairline overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-surface-sunken border-b border-hairline">
                    <h3 className="text-sm font-semibold text-ink">
                      {t('businessVerification.reviewLabels.authorizedRepresentative')}
                    </h3>
                    <button
                      type="button"
                      onClick={() => goToStep(4)}
                      className="text-xs font-medium text-[var(--nv-primary-color)] hover:opacity-80 transition-opacity min-h-[44px] px-2 flex items-center"
                    >
                      {t('businessVerification.actions.edit')}
                    </button>
                  </div>
                  <div className="p-4">
                    <dl className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
                      <dt
                        className={`text-ink-muted ${reviewMissing.repName ? 'text-danger' : ''}`}
                      >
                        {t('businessVerification.reviewLabels.name')}
                      </dt>
                      <dd
                        className={`text-ink ${reviewMissing.repName ? 'text-danger font-medium' : ''}`}
                      >
                        {representative.fullName || t('businessVerification.reviewLabels.missing')}
                      </dd>

                      <dt
                        className={`text-ink-muted ${reviewMissing.repRole ? 'text-danger' : ''}`}
                      >
                        {t('businessVerification.reviewLabels.role')}
                      </dt>
                      <dd
                        className={`text-ink ${reviewMissing.repRole ? 'text-danger font-medium' : ''}`}
                      >
                        {representativeRoles.find(
                          (r) => r.value === representative.role,
                        )?.label ?? (representative.role || t('businessVerification.reviewLabels.missing'))}
                      </dd>

                      <dt
                        className={`text-ink-muted ${reviewMissing.repEmail ? 'text-danger' : ''}`}
                      >
                        {t('businessVerification.reviewLabels.email')}
                      </dt>
                      <dd
                        className={`text-ink ${reviewMissing.repEmail ? 'text-danger font-medium' : ''}`}
                      >
                        {representative.email || t('businessVerification.reviewLabels.missing')}
                      </dd>

                      {representative.phone && (
                        <>
                          <dt className="text-ink-muted">{t('businessVerification.reviewLabels.phone')}</dt>
                          <dd className="text-ink">
                            {representative.phone}
                          </dd>
                        </>
                      )}

                      <dt
                        className={`text-ink-muted ${reviewMissing.repAuthorized ? 'text-danger' : ''}`}
                      >
                        {t('businessVerification.reviewLabels.authorized')}
                      </dt>
                      <dd
                        className={
                          representative.confirmedAuthorized
                            ? 'text-primary font-medium'
                            : 'text-danger font-medium'
                        }
                      >
                        {representative.confirmedAuthorized
                          ? t('businessVerification.reviewLabels.yes')
                          : t('businessVerification.reviewLabels.no')}
                      </dd>

                      {representative.authorizationEvidence && (
                        <>
                          <dt className="text-ink-muted">
                            {t('businessVerification.reviewLabels.authEvidence')}
                          </dt>
                          <dd className="text-primary-hover">
                            {representative.authorizationEvidence.name}
                          </dd>
                        </>
                      )}
                    </dl>
                  </div>
                </section>

                {/* Confirmation message */}
                <div className="rounded-md border border-info/30 bg-info-tint px-4 py-3 text-sm text-info">
                  {t('businessVerification.notes.submitDisclaimer')}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div
                role="alert"
                className="mt-6 rounded-lg border border-danger/30 bg-danger-tint px-4 py-3 text-sm text-danger"
              >
                {error}
              </div>
            )}

            {/* =========================================================== */}
            {/* Navigation                                                   */}
            {/* =========================================================== */}
            <div className="mt-8 flex flex-col sm:flex-row sm:justify-between gap-3">
              {currentStep > 0 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-hairline-strong px-6 h-11 text-sm font-medium text-ink-muted hover:bg-surface-sunken transition-colors order-2 sm:order-1 w-full sm:w-auto"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 19.5L8.25 12l7.5-7.5"
                    />
                  </svg>
                  {t('businessVerification.actions.back')}
                </button>
              ) : (
                <div className="order-2 sm:order-1" />
              )}

              {currentStep < kybSteps.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!stepValid[currentStep]}
                  className="inline-flex items-center justify-center gap-2 rounded-md px-6 h-11 text-sm font-semibold text-white transition-opacity hover:brightness-90 disabled:cursor-not-allowed disabled:opacity-50 order-1 sm:order-2 w-full sm:w-auto"
                  style={{ backgroundColor: 'var(--nv-primary-color)' }}
                >
                  {t('businessVerification.actions.next')}
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8.25 4.5l7.5 7.5-7.5 7.5"
                    />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 rounded-md px-8 h-12 text-base font-semibold text-white transition-opacity hover:brightness-90 disabled:cursor-not-allowed disabled:opacity-50 order-1 sm:order-2 w-full sm:w-auto"
                  style={{ backgroundColor: 'var(--nv-primary-color)' }}
                >
                  {submitting && (
                    <span
                      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                      aria-hidden="true"
                    />
                  )}
                  {submitting ? t('businessVerification.actions.submitting') : t('businessVerification.actions.submit')}
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* ================================================================= */}
      {/* Footer                                                            */}
      {/* ================================================================= */}
      <Footer
        branding={
          branding ?? {
            logoUrl: null,
            primaryColor: null,
            supportEmail: null,
            termsUrl: null,
            privacyUrl: null,
          }
        }
      />
    </div>
  );
}
